import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Power, Dices, Sparkles, Pencil, Mail, Wand2, Trash2, Bot, Scale } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";
import { gbp, fmtDate } from "../../lib/format";
import { recommendPricing, recommendFairnessCap, divideInstantWins, randomSkill, daysFromNowISO } from "../../lib/aiTools";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";

const EMPTY = {
  title: "", description: "", prize_title: "", prize_value: 0, cash_alternative: 0, hero_image: "",
  ticket_price: 1.99, total_tickets: 1000, min_threshold: 0, duration_days: 5, ends_at: "", featured: false,
  skill_question: { question: "", options: ["", "", "", ""], answer_index: 0 },
  instant_wins_enabled: false, instant_wins: [], instant_pot: 0, keep_profit: 45,
  live_draw_enabled: false, live_draw_url: "",
  free_postal_enabled: true, metadata_tags: [], subscriber_eligible: false, max_tickets_per_user: 0,
};

const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d - off * 60000).toISOString().slice(0, 16); };

const In = ({ label, hint, ...p }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{label}</label>
    <input className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" {...p} />
    {hint && <p className="mt-1 text-[11px] text-[var(--text-muted)]">{hint}</p>}
  </div>
);
const Tog = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2 cursor-pointer">
    <span className="text-sm text-white">{label}</span>
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="accent-[var(--neon-pink)] h-4 w-4" />
  </label>
);
const Section = ({ color = "cyan", title, action, children }) => (
  <div className={`rounded-xl border p-3 space-y-3 ${color === "cyan" ? "border-[rgba(0,229,255,.4)]" : "border-[rgba(255,45,149,.4)]"}`}>
    <div className="flex items-center justify-between">
      <p className={`text-xs font-display uppercase tracking-widest glow-${color}`}>{title}</p>
      {action}
    </div>
    {children}
  </div>
);

export default function AdminCompetitions() {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  const load = () => api.get("/competitions").then(({ data }) => setComps(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setF(EMPTY); setAiPrompt(""); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setF({
      ...EMPTY, ...c, ends_at: toLocalInput(c.ends_at),
      skill_question: { question: c.skill_question?.question || "", options: c.skill_question?.options || ["", "", "", ""], answer_index: c.skill_question?.answer_index ?? 0 },
      metadata_tags: c.metadata_tags || [], instant_wins: c.instant_wins || [], instant_pot: 0, keep_profit: 45,
    });
    setAiPrompt(""); setOpen(true);
  };

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setNum = (k) => (e) => setF((s) => ({ ...s, [k]: Number(e.target.value) }));

  // ---- AI tools ----
  const aiDescribe = async () => {
    const prompt = aiPrompt || f.title || f.prize_title;
    if (!prompt) { toast.error("Type what you're giving away first."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/ai/describe", { prompt });
      setF((s) => ({ ...s, title: data.title || s.title, prize_title: s.prize_title || data.title, description: data.copy || s.description, metadata_tags: data.tags || s.metadata_tags }));
      toast.success("AI copy generated.");
    } catch (e) { toast.error(getErr(e)); } finally { setAiBusy(false); }
  };
  const aiPricing = (mode) => {
    const r = recommendPricing(f.prize_value, instantPotValue(), mode);
    setF((s) => ({ ...s, ...r }));
    toast.success(`AI set ${mode === "cheap" ? "cheap & high-volume" : "balanced"} pricing.`);
  };
  const aiFairness = () => { setF((s) => ({ ...s, max_tickets_per_user: recommendFairnessCap(f.total_tickets) })); toast.success("AI set a fairness cap."); };
  const aiDivide = () => {
    const wins = divideInstantWins(f.instant_pot, f.total_tickets);
    if (wins.length === 0) { toast.error("Enter a total instant-win value first."); return; }
    setF((s) => ({ ...s, instant_wins_enabled: true, instant_wins: wins }));
    toast.success(`AI divided ${gbp(f.instant_pot)} into ${wins.length} instant prizes.`);
  };
  const aiSkill = () => { setF((s) => ({ ...s, skill_question: randomSkill() })); toast.success("Skill question generated."); };

  const instantPotValue = () => (f.instant_wins || []).reduce((a, b) => a + Number(b.value || 0), 0) || Number(f.instant_pot || 0);

  const addInstantPrize = () => setF((s) => ({ ...s, instant_wins: [...(s.instant_wins || []), { ticket_number: Math.floor(Math.random() * (s.total_tickets || 1000)) + 1, prize_type: "cash", label: "Cash Instant Win", value: 50 }] }));
  const updInstant = (i, k, v) => setF((s) => { const arr = [...s.instant_wins]; arr[i] = { ...arr[i], [k]: k === "value" || k === "ticket_number" ? Number(v) : v }; return { ...s, instant_wins: arr }; });
  const delInstant = (i) => setF((s) => ({ ...s, instant_wins: s.instant_wins.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!f.title || !f.prize_title) { toast.error("Title and prize title are required."); return; }
    if (!f.skill_question.question || f.skill_question.options.filter(Boolean).length < 2) { toast.error("Add a skill question with at least 2 options."); return; }
    setSaving(true);
    const payload = {
      title: f.title, description: f.description, prize_title: f.prize_title, prize_value: Number(f.prize_value), cash_alternative: Number(f.cash_alternative),
      hero_image: f.hero_image, ticket_price: Number(f.ticket_price), total_tickets: Number(f.total_tickets), min_threshold: Number(f.min_threshold),
      ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : daysFromNowISO(f.duration_days),
      featured: f.featured, skill_question: f.skill_question,
      instant_wins_enabled: f.instant_wins_enabled, instant_wins: (f.instant_wins || []).map((w) => ({ ticket_number: Number(w.ticket_number), prize_type: w.prize_type, label: w.label, value: Number(w.value) })),
      live_draw_enabled: f.live_draw_enabled, live_draw_url: f.live_draw_url, free_postal_enabled: f.free_postal_enabled,
      metadata_tags: typeof f.metadata_tags === "string" ? f.metadata_tags.split(",").map((x) => x.trim()).filter(Boolean) : f.metadata_tags,
      subscriber_eligible: f.subscriber_eligible, max_tickets_per_user: Number(f.max_tickets_per_user),
    };
    try {
      if (editId) { await api.put(`/admin/competitions/${editId}`, payload); toast.success("Competition updated."); }
      else { await api.post("/admin/competitions", payload); toast.success("Competition created."); }
      setOpen(false); load();
    } catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(getErr(e)); } };
  const duplicate = (c) => act(() => api.post(`/admin/competitions/${c.id}/duplicate`), "Duplicated.");
  const toggle = (c) => act(() => api.post(`/admin/competitions/${c.id}/toggle-live`), "Toggled live status.");
  const draw = (c) => { if (window.confirm(`Run the draw for "${c.title}"? This is final.`)) act(() => api.post(`/admin/competitions/${c.id}/draw`), "Draw completed."); };
  const postal = (c) => { const email = window.prompt("User email for postal entry:"); if (!email) return; const qty = Number(window.prompt("Quantity:", "1")) || 1; act(() => api.post(`/admin/competitions/${c.id}/postal-entry`, { user_email: email, quantity: qty }), "Postal entry added."); };

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-2xl text-white">Competitions</h2>
        <NeonButton variant="pink" onClick={openNew} data-testid="admin-competitions-create-button"><Plus className="h-4 w-4" /> New</NeonButton>
      </div>

      <NeonCard variant="soft" className="mt-4" innerClassName="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-competitions-table">
            <thead><tr className="text-left text-[var(--text-muted)] border-b border-white/10"><th className="p-3">Title</th><th>Status</th><th>Price</th><th>Sold</th><th>IW</th><th>Ends</th><th className="text-right p-3">Actions</th></tr></thead>
            <tbody>
              {comps.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="p-3 text-white max-w-[200px] truncate">{c.title}</td>
                  <td><Pill color={c.status === "active" ? "green" : "grey"}>{c.status}</Pill></td>
                  <td>{gbp(c.ticket_price)}</td>
                  <td>{c.tickets_sold}/{c.total_tickets}</td>
                  <td>{c.instant_wins_enabled ? <Pill color="cyan">{c.instant_wins_remaining ?? "✓"}</Pill> : <span className="text-[var(--text-muted)]">—</span>}</td>
                  <td className="text-[var(--text-muted)]">{fmtDate(c.ends_at)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button title="Edit" onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-white/10 text-[var(--neon-cyan)]" data-testid={`edit-comp-${c.id}`}><Pencil className="h-4 w-4" /></button>
                      <button title="Duplicate" onClick={() => duplicate(c)} className="p-2 rounded-lg hover:bg-white/10 text-white"><Copy className="h-4 w-4" /></button>
                      <button title="Toggle Live" onClick={() => toggle(c)} className="p-2 rounded-lg hover:bg-white/10 text-[var(--neon-green)]"><Power className="h-4 w-4" /></button>
                      <button title="Postal Entry" onClick={() => postal(c)} className="p-2 rounded-lg hover:bg-white/10 text-white"><Mail className="h-4 w-4" /></button>
                      <button title="Run Draw" onClick={() => draw(c)} className="p-2 rounded-lg hover:bg-white/10 text-[var(--neon-pink)]" data-testid={`draw-comp-${c.id}`}><Dices className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NeonCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--surface)] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl">{editId ? "Edit" : "New"} Competition</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">AI-assisted setup — generate copy, pricing, instant wins and a skill question.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* AI Describer */}
            <Section color="cyan" title={<span className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> AI Describer</span>}>
              <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g. brand new tesla model 3, neon vibe, £40000" className="w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="ai-describe-prompt" />
              <NeonButton variant="outlinePink" className="w-full" onClick={aiDescribe} loading={aiBusy} data-testid="ai-describe-generate"><Wand2 className="h-4 w-4" /> Generate Copy</NeonButton>
            </Section>

            <In label="Title" value={f.title} onChange={set("title")} />
            <div><label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Description</label>
              <textarea value={f.description} onChange={set("description")} rows={3} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" /></div>
            <In label="Prize title" value={f.prize_title} onChange={set("prize_title")} />
            <In label="Hero image URL" value={f.hero_image} onChange={set("hero_image")} />
            <In label="Category / tags (comma separated)" value={Array.isArray(f.metadata_tags) ? f.metadata_tags.join(", ") : f.metadata_tags} onChange={set("metadata_tags")} hint="e.g. Tech, Holidays, Cars, Cash, Watches" />

            <div className="grid grid-cols-2 gap-3">
              <In label="Prize value (£)" type="number" value={f.prize_value} onChange={setNum("prize_value")} />
              <In label="Cash alternative (£)" type="number" value={f.cash_alternative} onChange={setNum("cash_alternative")} />
            </div>

            {/* AI Ticket Pricing */}
            <Section color="cyan" title={<span className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> AI Ticket Pricing</span>}>
              <p className="text-[11px] text-[var(--text-muted)]">Suggests ticket price, quantity &amp; break-even from your prize value + instant wins (keeps ~30% profit).</p>
              <div className="grid grid-cols-2 gap-2">
                <NeonButton variant="outlinePink" onClick={() => aiPricing("cheap")} data-testid="ai-pricing-cheap"><Bot className="h-4 w-4" /> Cheap &amp; High Volume</NeonButton>
                <NeonButton variant="outlinePink" onClick={() => aiPricing("balanced")} data-testid="ai-pricing-balanced"><Scale className="h-4 w-4" /> Average / Balanced</NeonButton>
              </div>
            </Section>

            <div className="grid grid-cols-2 gap-3">
              <In label="Ticket price (£)" type="number" step="0.01" value={f.ticket_price} onChange={setNum("ticket_price")} />
              <In label="Total tickets" type="number" value={f.total_tickets} onChange={setNum("total_tickets")} />
              <In label="Min threshold (break-even)" type="number" value={f.min_threshold} onChange={setNum("min_threshold")} />
              <In label="Duration (days)" type="number" value={f.duration_days} onChange={setNum("duration_days")} hint="Used if no exact end date set" />
              <In label="Exact end date (optional)" type="datetime-local" value={f.ends_at} onChange={set("ends_at")} />
            </div>

            {/* Fairness cap */}
            <Section color="pink" title="Fairness Cap" action={<button onClick={aiFairness} className="text-xs text-[var(--neon-cyan)] flex items-center gap-1" data-testid="ai-fairness"><Bot className="h-3.5 w-3.5" /> AI Recommend</button>}>
              <In label="Max tickets per person (0 = global limit)" type="number" value={f.max_tickets_per_user} onChange={setNum("max_tickets_per_user")} />
            </Section>

            {/* Instant wins */}
            <Section color="cyan" title={<span className="flex items-center gap-1">⚡ Instant Wins</span>}>
              <Tog label="Enable instant wins" checked={f.instant_wins_enabled} onChange={(e) => setF((s) => ({ ...s, instant_wins_enabled: e.target.checked }))} testid="toggle-instant-wins" />
              {f.instant_wins_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-2 items-end">
                    <In label="Total instant-win value (£)" type="number" value={f.instant_pot} onChange={setNum("instant_pot")} />
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Keep as profit</label>
                      <div className="mt-1 flex gap-2">
                        {[45, 50].map((p) => (
                          <button key={p} onClick={() => setF((s) => ({ ...s, keep_profit: p }))} className={`flex-1 rounded-full px-3 py-2 text-sm ${f.keep_profit === p ? "bg-[var(--neon-cyan)] text-black" : "border border-white/15 text-[var(--text-muted)]"}`}>{p}% profit</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <NeonButton variant="outlinePink" className="w-full" onClick={aiDivide} data-testid="ai-divide-instant"><Bot className="h-4 w-4" /> AI Recommend &amp; Divide</NeonButton>
                  <div className="space-y-2">
                    {(f.instant_wins || []).map((w, i) => (
                      <div key={i} className="grid grid-cols-[80px_1fr_90px_36px] gap-2 items-center">
                        <input type="number" value={w.ticket_number} onChange={(e) => updInstant(i, "ticket_number", e.target.value)} className="bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white text-xs" title="Ticket #" />
                        <input value={w.label} onChange={(e) => updInstant(i, "label", e.target.value)} className="bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                        <select value={w.prize_type} onChange={(e) => updInstant(i, "prize_type", e.target.value)} className="bg-[var(--surface-2)] border border-white/10 rounded px-1 py-1.5 text-white text-xs"><option value="cash">Cash</option><option value="credit">Credit</option><option value="physical">Physical</option></select>
                        <button onClick={() => delInstant(i)} className="text-[var(--neon-pink)] flex justify-center"><Trash2 className="h-4 w-4" /></button>
                        <input type="number" value={w.value} onChange={(e) => updInstant(i, "value", e.target.value)} className="col-span-1 bg-[var(--surface-2)] border border-white/10 rounded px-2 py-1.5 text-[var(--neon-green)] text-xs" title="£ value" />
                      </div>
                    ))}
                  </div>
                  <NeonButton variant="outlineCyan" className="w-full" onClick={addInstantPrize} data-testid="add-instant-prize"><Plus className="h-4 w-4" /> Add Instant Prize</NeonButton>
                </>
              )}
            </Section>

            {/* Skill question */}
            <Section color="pink" title="Skill Question" action={<button onClick={aiSkill} className="text-xs text-[var(--neon-gold)] flex items-center gap-1" data-testid="ai-skill-generate"><Sparkles className="h-3.5 w-3.5" /> Auto-generate</button>}>
              <p className="text-[11px] text-[var(--text-muted)]">A genuine skill question is a UK legal requirement for paid prize competitions.</p>
              <In label="Question" value={f.skill_question.question} onChange={(e) => setF((s) => ({ ...s, skill_question: { ...s.skill_question, question: e.target.value } }))} />
              <div className="grid grid-cols-2 gap-2">
                {f.skill_question.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="radio" name="answer" checked={f.skill_question.answer_index === i} onChange={() => setF((s) => ({ ...s, skill_question: { ...s.skill_question, answer_index: i } }))} className="accent-[var(--neon-green)]" />
                    <input value={o} onChange={(e) => setF((s) => { const opts = [...s.skill_question.options]; opts[i] = e.target.value; return { ...s, skill_question: { ...s.skill_question, options: opts } }; })} placeholder={`Option ${i + 1}`} className="flex-1 bg-[var(--surface-2)] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm" />
                  </div>
                ))}
              </div>
            </Section>

            <div className="grid grid-cols-2 gap-2">
              <Tog label="Featured" checked={f.featured} onChange={(e) => setF((s) => ({ ...s, featured: e.target.checked }))} />
              <Tog label="Subscriber eligible" checked={f.subscriber_eligible} onChange={(e) => setF((s) => ({ ...s, subscriber_eligible: e.target.checked }))} />
              <Tog label="Free postal entry" checked={f.free_postal_enabled} onChange={(e) => setF((s) => ({ ...s, free_postal_enabled: e.target.checked }))} />
              <Tog label="Live draw" checked={f.live_draw_enabled} onChange={(e) => setF((s) => ({ ...s, live_draw_enabled: e.target.checked }))} />
            </div>
            {f.live_draw_enabled && <In label="Live draw URL" value={f.live_draw_url} onChange={set("live_draw_url")} />}
          </div>
          <DialogFooter>
            <NeonButton variant="ghost" onClick={() => setOpen(false)}>Cancel</NeonButton>
            <NeonButton variant="pink" onClick={save} loading={saving} data-testid="admin-competition-save">{editId ? "Save Changes" : "Create"}</NeonButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
